import React, { useCallback, useEffect, useRef, useState } from "react"; // 212 §22 — `import React`: react/react-in-jsx-scope's own remedy (189 F.1)
import type { ReactNode } from "react";
import {
  Trash2,
  ChevronUp,
  ChevronDown,
  Plus,
  Clock,
  Play,
} from "lucide-react";
import {
  listSubtasksByTask,
  listSubtasksByEvent,
  createSubtask,
  updateSubtask,
  deleteSubtask,
} from "../api";
import { useConfirmAndUndo, UndoToast } from "./useConfirmAndUndo";
import { fmtElapsed } from "../utils";
import { useTranslation } from "../i18n";
import { resolveSubtaskText, subtaskStatusOf } from "../lib/subtaskHelpers";
import type { Subtask } from "@shared/entities";
import AssigneePicker from "./AssigneePicker";
import { InitialsBadge, AddButton, ModalLayout, ListNotLoaded } from "./SharedUI";
import { useReadMark, usePageListContext } from "../hooks/usePageList"; // 212 §22
import { ListLoadContext, PAGE_LISTS } from "../lib/listLoad";
import SelfTick from "./SelfTick";
// BRIEF 95 §3a(a) — one date control, DD/MM/YY at rest and ISO on the wire.
import DateField, { dmy2 } from "./DateField";
// BRIEF 117 §2a — the parent row's own pill, not a second one.
import StatusPill, { STATUS_PILL_W } from "./StatusPill"; // 118 §2, 136 §1
import { pickableStatusCodes } from "../lib/taskCounters"; // 136 §1
import { useTaxonomy } from "../hooks/useTaxonomy";
import { getTaskStatusMeta } from "../lib/taxonomyHelpers";
import { CT, RAD, FS, Z } from "../lib/tokens";

// Phase M+7 — lean subtask checklist used inside Task / AgendaEvent edit
// modals and under the CV/CCV/pop-out task rows. When `parentTaskId` /
// `parentEventId` is provided the component loads from the API and persists
// each change immediately. When neither is set (creating a new parent), the
// component holds subtasks in local state and exposes them via
// `onLocalChange` for the parent's save path to flush server-side after the
// parent row is created.
//
// Round 13 (C75) — ONE canonical row, identical in the app and the pop-out.
// BRIEF 91 §2a — AND ITS ORDER IS NOW THE PARENT TASK ROW'S:
//   [assignee badge or "+"] [timer ▷ + time-log watch]
//   [description — adaptive, truncating] [due date — 10-char DD/MM/YYYY width]
//   [reorder chevrons] [done checkbox] [archive toggle]
//
// The old order led with the two time controls and buried the assignee fourth,
// behind the checkbox — a reading order no other row in this application uses.
// `CaseTaskRow` puts Assigned before the timer cell (CaseTaskRow.tsx:510-616),
// its status and due columns after the title, and its row actions last, and a
// subtask row is a task row's smaller sibling.
//
// THE TIMER AND THE WATCH ARE ONE COLUMN, not two, for the same reason: the
// parent row draws both inside a single `<td>` (`tdTimer`, CaseTaskRow.tsx:242)
// and the brief's wanted order names one "timer". They shared a 6px flex gap as
// separate columns; they share a 2px one inside a cell.
//
// §2c — WHAT IS FIXED AND WHAT IS NOT. Everything except the description is
// fixed-width and `flexShrink: 0`: assignee and timer at the left edge, the due
// date and the row-action tail at the right. The description is the only
// adaptive column, and the only one that truncates. That is what lets §2d's add
// row mirror the persisted rows exactly — same left edge, same right edge — by
// reserving the same two numbers rather than by hoping two sums agree.
// The delete bin is REPLACED by the archive toggle on persisted rows:
// archiving dims the row in place, drops it from every counter/badge/
// due-today bucket, and the same control restores it. (Unsaved local rows in
// the create modal keep a plain remove — they aren't data yet.)
//
// Round 13 (C69/C70) — per-ROW rights instead of the old all-or-nothing
// `canWrite`: full-edit viewers keep everything; a viewer whose ONLY claim is
// holding a row (a delegatee) can tick / time / log HIS row, all other rows
// render read-only. The title is an input only when the row is editable —
// read-only rows render a truncating span, which also fixes the pop-out's
// "empty title" collapse (a flex input with min-width:0 shrank to nothing
// next to the fixed-width controls at 330px; the span ellipsises instead and
// editable inputs keep a usable minimum width).
//
// Time-logging integration paths surface as icon buttons leading each row;
// the actual TE-modal-open and timer-start handlers live in App.jsx — this
// component just calls back via `onLogTime` / `onStartTimer`. Done-toggle
// emits `onCheckDone` so the parent can run the canonical completion prompt.

export default function SubtaskChecklist({
  parentTaskId,
  parentEventId,
  initialSubtasks,
  controlledItems,
  onLocalChange,
  onItemsChange,
  onLogTime,
  onStartTimer,
  onCheckDone,
  isTimerRunning,
  runningSubtaskId,
  /* Round 15 (C92) — the host's active timers for THIS parent's subtasks
     (array of { subtaskId, paused, elapsed, startedAt }): the row owning the
     RUNNING timer swaps its triangle for the live elapsed readout, a PAUSED
     one shows triangle + static total, exactly like the parent-row timer
     cell. The legacy isTimerRunning/runningSubtaskId pair stays as the
     fallback for hosts that don't pass it. */
  subtaskTimers,
  isAdmin,
  canWrite,
  /* Phase 29 C59/C64, round 13 C70 — the unified AssigneePicker now mounts on
     EVERY editable surface, the pop-out included (the old read/tick-only
     restriction is lifted). Read-only viewers still get the initials badge. */
  co = [],
  currentUser,
  canAssignAll = false,
  // §6 E3 — the section's own controls, rendered under its heading.
  headerSlot,
  assignable = false,
  /* Round 14 (C83ii) — pop-out/column host: drop the reorder chevrons and
     the archive toggle (those controls live in CV/CCV/Dashboard panes
     only); ticking, timing, logging, title/date/assignee edits and the add
     row keep their C81 parity. */
  hideRowActions = false,
  /* 109 §1c — the HOST's lock state. This component had no lock prop at all:
     rows were editable inputs at all times, in every host, locked or not.
     Locked gates STRUCTURE only — text, date, add, archive, reorder,
     assignee — rendering the rows' existing read-only twins. The done
     CHECKBOX stays live (enumerated: completing a subtask is normal-mode
     work, like ticking a task), and so do the timer/log controls beside it.
     Default false = today's behaviour for any host that does not thread it. */
  locked = false,
  /* 110 §1b — the Dashboard's rule: its drawers are VIEW-ONLY and editing
     means jumping to the matter. readOnly suppresses exactly the five
     prohibitions the ruling enumerates — add row/button, editable fields
     (text/date/assignee), the done checkbox, archive/reorder — while the
     LOOK stays the list drawers' (columns, initials, timer controls: a
     timer is not an edit and is not on the list; enumerated in the PR).
     Distinct from `locked`, which is normal-mode structure gating and
     leaves the checkbox live. */
  readOnly = false,
}: any) {
  const { t } = useTranslation();
  /* BRIEF 117 §2a — the SAME ladder the parent pill reads. The checklist asks
     the taxonomy itself rather than taking entries as a prop: it mounts under
     six hosts (CV/CCV task + event drawers, the Dashboard's, the pop-out's,
     both modals) and threading one more prop through all six is how the two
     pills come to read different ladders. `useTaxonomy` is the app's own cache. */
  const { entries: taskStatusEntries } = useTaxonomy("task_status");
  /* 136 §1 — the menu's rows, resolved ONCE per render rather than per row.
     Same ladder and same exclusion as the master pill (`pickableStatusCodes`),
     because the comment above is the reason this component reads the taxonomy
     itself: two pills that resolve their vocabulary differently is exactly what
     it exists to prevent. */
  const stOptions = pickableStatusCodes(taskStatusEntries).map((code) => ({
    code,
    cfg: getTaskStatusMeta(code, taskStatusEntries),
  }));
  const [internalItems, setInternalItems] = useState(() =>
    Array.isArray(initialSubtasks) ? initialSubtasks : [],
  );
  const [loading, setLoading] = useState(false);
  /* Round 16 (C100) — the Add row stages its own assignee alongside text +
     date, so a delegated subtask is one step, not create-then-assign. */
  /* BRIEF 108 §1 — the permanent add row became a small modal behind a
     "+ Add" on the heading line. The draft survives as the modal's form. */
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState<any>({
    text: "",
    dueDate: "",
    assignedCoId: null,
  });
  const persisted = parentTaskId != null || parentEventId != null;
  // Phase 7 stage 3 part 3 — controlled mode for the create-modal template
  // preview. When controlledItems is provided and we're not persisted, the
  // parent owns the items array (so it can inject/strip template-derived
  // items as type/dueDate/toggle changes). Mutations still flow through
  // onLocalChange exactly as before.
  const useControlled = !persisted && Array.isArray(controlledItems);
  const items = useControlled ? controlledItems : internalItems;
  const setItems = useControlled ? () => {} : setInternalItems;
  // canWrite = FULL edit (add row, text/date edits, reorder, archive,
  // assignment). Defaults true to preserve the M+7 task-modal behaviour.
  const writable = canWrite !== false;
  // C69 — a row is "mine" when it is assigned to the viewer; mine rows stay
  // tickable/timeable even for viewers without the full-edit grant (the
  // delegatee path — server-side this is exactly the C68a carve-out).
  const isMine = (s: any) =>
    currentUser != null && !!s && s.assignedCoId === currentUser;

  // C75 — fixed column widths. Round 14 (C83iii) — the date column is sized
  // TIGHT to the value: the characters of the row font plus only the control's
  // own 2+2px padding and 1+1px border.
  // BRIEF 95 §3a(a) — 10ch -> 8ch, because the value is DD/MM/YY now. 10ch was
  // not merely generous, it was SHORT: measured in Chromium at this row's 11px
  // font, `calc(10ch + 6px)` renders `06/18/2026` as `06/18/202` — a `ch` is
  // the width of "0", and the two slashes are wider. Eight characters at the
  // same budget fit with room to spare (measured: `18/06/26`, uncut).
  const DATE_W = "calc(8ch + 6px)";
  const _hasTimerCol =
    typeof onLogTime === "function" || typeof onStartTimer === "function";
  /* BRIEF 115 §4 — THE BLOCK IS THE MAIN ROW'S, MEASURED RATHER THAN CHOSEN:
     tdTimer (CaseTaskRow) renders [play 20][1][clock 18] = a 39px block with
     an 8px ink gap between the glyphs. 112 §1e aligned the ICONS (12 == 12);
     what still drifted was this cell's own anatomy — two 26px slots with a
     2px flex gap centred each button loosely, so the pair sat 9px apart
     (16px of ink) and the block ran 47px wide where the parent row's runs
     39. The slots THEMSELVES stay, at the buttons' own widths: 92 §3c's
     reason holds (a done row loses its triangle and its clock must hold the
     column), only the numbers stop being invented.
       PLAY_W  20 = Play(12) + tdTimer's own 4+4 padding
       LOG_W   18 = Clock(12) + its 3+3 padding
       the 1 between = tdTimer's marginLeft: 1 */
  const PLAY_W = 20;
  const LOG_W = 18;
  const TIMER_W = PLAY_W + LOG_W + 1;
  /* §1c — the tail (chevrons + archive) is structure; under a lock the
     standalone-checkbox arm below takes over, exactly as the pop-out host's
     `hideRowActions` already renders it. */
  const _rowActions = writable && !hideRowActions && !locked && !readOnly;
  /* 91 §2c — the right-hand tail, ONE number used by the persisted rows and by
     the add row so their date columns land on the same x.
       chevrons 2×18 + 4 = 40 · done checkbox 18 · archive 24 · two 4px gaps
     C88's enlarged chevron hit targets are inside the 40.
     ZERO in the pop-out/column host: `hideRowActions` strips the chevrons and
     the archive toggle there (C83ii, a width decision), so there is no tail to
     mirror and reserving one would give back the width C83ii reclaimed. The
     done checkbox is then the last fixed column and the add row's "+ Add"
     trails at its natural width, exactly as both did before this brief.
     BRIEF 92 §3b — 86 → 90, because the checkbox grew by 4 and this number is
     the SUM ABOVE and not a constant that happens to work. A tail that no
     longer adds up is how the add row's date field starts drifting off the
     column it is supposed to sit under.
     BRIEF 117 §2a — 90 → 137, the same sum with the pill's 65 where the
     checkbox's 18 was: chevrons 40 · pill 65 · bin 24 · two 4px gaps. */
  /* BRIEF 117 §2a — THE DONE CHECKBOX IS A STATUS PILL, and the slot it sits
     in grew from 18 to hold one.

     THE MEASUREMENT THAT SHAPES THIS: `Subtask` has NO status column
     (`schema.prisma:1151-1189` — `done Boolean`, and `parentTaskId` /
     `parentEventId` / `assignedCoId` beside it; there is no third state to
     store). So "the done state is then a pill value like everywhere else" is
     delivered over the TWO values the row can hold — the firm's `todo` and
     `completed` rungs, resolved through the SAME `getTaskStatusMeta` and the
     same `task_status` taxonomy the parent pill reads, rendered by the same
     `StatusPill`, and written through the SAME write path (118 §2 renamed
     `toggleDone` to `cycleStatus` when the third rung arrived; 136 §1 renamed
     it again to `setStatus`, because a menu does not cycle — it still runs the
     C76 completion prompt on the way in). The "ongoing" rung a task has is
     REGISTERED as a successor, not invented here: it needs a column, and a GUI
     brief does not mint one.

     THE WIDTH IS `STATUS_PILL_W`, THE PARENT PILL'S OWN — not a number picked
     here. It is what makes §2b's alignment exact rather than approximate: see
     StatusPill.tsx, where the constant and its measurement live. It also does
     92 §2c's job on the way (a row reading "To do" and a row reading
     "Completed" must not put the column before them in two places). */
  const PILL_W = STATUS_PILL_W;
  const TAIL_W = _rowActions ? 40 + PILL_W + 24 + 8 : 0;
  const showPicker = assignable && writable && !locked && !readOnly; // §1c — assignee is structure
  const [assignEdit, setAssignEdit] = useState<any>(null); // row index being picked
  /* 110 §1c — the DESCRIPTION leaves the structure gate (RULED): like a note,
     it is quiet text in every state; a click (rights permitting, lock or no
     lock) reveals the editable field, blur/Enter commit through the existing
     patch, Escape reverts. The revert is a REF, never a state flag — 109's
     NotesDrawer lesson: state cannot outrun the synchronous native blur, so
     an Escape routed through setState is read by onBlur one render too late
     and SAVES what it meant to discard. */
  const [textEdit, setTextEdit] = useState<any>(null); // row key being edited
  const cancelTextRef = useRef(false);
  const _hasAssignees =
    !showPicker && items.some((s) => s && s.assignedCoId != null);
  const ASSIGN_W = showPicker ? (assignEdit != null ? 108 : 22) : _hasAssignees ? 22 : 0;
  // 154B §2 — the ref callback that focused the mounted <select> and called
  // showPicker() on it is `autoOpen` now: the list it opens is the app's own
  // menu, which the component opens from its own state rather than by asking
  // the DOM to. (The DATE picker below still needs the native call.)
  // Open the native date picker on click without a persistent calendar icon
  // (icon hidden via the .stDate CSS rule).
  const openPicker = (e: any) => {
    const el = e.currentTarget;
    if (el.disabled) return;
    try {
      el.showPicker?.();
    } catch {
      /* showPicker can throw if unsupported / not user-activated — ignore */
    }
  };

  // Server load when a parent id is known and no initial seed was provided.
  /* 212 §22 — the task and event windows' read (the only one: a matter page
     hands its rows in `initialSubtasks`) asked the 210 §1 way; still failing,
     the heading says « La liste n'a pas pu être chargée » with « Réessayer »
     where « No subtasks yet. » stood — and offers no « Add »: a subtask added
     to a list not loaded would hand the host a list without the others. */
  const { entry: subRead, again: subAgain, ask: askSubtasks } = useReadMark(PAGE_LISTS.subtasks);
  const subLists = usePageListContext(subRead);
  const subFailed = persisted && !Array.isArray(initialSubtasks) && Boolean(subRead.failure);
  useEffect(() => {
    if (!persisted) return;
    if (Array.isArray(initialSubtasks)) {
      setInternalItems(initialSubtasks);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void askSubtasks("the subtasks", () =>
      parentTaskId != null ? listSubtasksByTask(parentTaskId) : listSubtasksByEvent(parentEventId),
    ).then((rows) => {
      if (cancelled) return;
      setInternalItems(rows ?? []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentTaskId, parentEventId, subAgain]);

  function emitLocal(next: any) {
    if (!useControlled) setInternalItems(next);
    if (!persisted && typeof onLocalChange === "function") onLocalChange(next);
  }

  // After every persisted CRUD, lift the latest items up so the parent
  // (CaseView/App) can refresh tk[i].subtasks. The bell aggregation reads
  // tk[*].subtasks; without this hand-off, fresh subtask edits never reach
  // the notifications array until the next full task fetch.
  function applyPersisted(next: any) {
    setItems(next);
    if (typeof onItemsChange === "function") onItemsChange(next);
  }

  async function add() {
    if (!writable) return;
    const text = draft.text.trim();
    if (!text) return;
    const dueDate = draft.dueDate || null;
    /* Round 16 (C100) — the staged assignee rides the create (persisted:
       the server materializes delegation as for any assignment; local:
       the create-modal flush already persists assignedCoId — C59). The
       chip resets with the rest of the draft. */
    const assignedCoId =
      showPicker && draft.assignedCoId ? +draft.assignedCoId : null;
    const sortOrder = items.length;
    if (persisted) {
      try {
        const payload: any = { text, dueDate, sortOrder };
        if (assignedCoId != null) payload.assignedCoId = assignedCoId;
        if (parentTaskId != null) payload.parentTaskId = parentTaskId;
        if (parentEventId != null) payload.parentEventId = parentEventId;
        const row = await createSubtask(payload);
        applyPersisted([...items, row]);
        setDraft({ text: "", dueDate: "", assignedCoId: null });
        setAddOpen(false); // §1c — on add, close; a failed create keeps it open
      } catch (e) {
        console.warn("Subtask create failed:", (e as any).message);
        // Phase 30 (D-3) — surface the failure (was console-only).
        window.__lxToast?.("Subtask: " + ((e as any)?.message || "create failed"), "error");
      }
    } else {
      const row = {
        _localId: `tmp-${Date.now()}-${items.length}`,
        text,
        dueDate,
        done: false,
        sortOrder,
        ...(assignedCoId != null ? { assignedCoId } : {}),
      };
      emitLocal([...items, row]);
      setDraft({ text: "", dueDate: "", assignedCoId: null });
      setAddOpen(false); // §1c — on add, close
    }
  }

  // selfOk — C69: the row's own assignee may flip `done` without the full
  // grant (server C68a accepts exactly that single-field patch).
  // Round 14 (C88) — OPTIMISTIC with rollback: the old shape awaited the
  // PUT before touching state, so every click sat dead for a round-trip
  // (one half of the "lame" report), and a failed PUT vanished silently
  // (the other half — the archive toggle looked like a no-op). The row now
  // updates immediately; a failed PUT rolls the list back and keeps the
  // console warn. Returns whether the change stuck.
  /* 118 §2 — `localOnly` is applied to the row in state and NOT sent. It
     exists for the one field the server derives rather than accepts: `done`
     follows `status` at the write boundary, and putting both on the wire would
     widen the C68a self-toggle patch past the one key it tests for. */
  async function patch(idx: any, partial: any, selfOk?: any, localOnly?: any) {
    if (!writable && !selfOk) return false;
    const cur = items[idx];
    if (!cur) return false;
    const before = items;
    const next = items.map((s, i) =>
      i === idx ? { ...s, ...partial, ...(localOnly || {}) } : s,
    );
    if (!persisted) {
      emitLocal(next);
      return true;
    }
    applyPersisted(next);
    if (!cur.id) return true;
    try {
      await updateSubtask(cur.id, partial);
      return true;
    } catch (e) {
      console.warn("Subtask update failed:", (e as any).message);
      applyPersisted(before);
      // Phase 30 (D-3) — rollback existed; the toast was missing.
      window.__lxToast?.("Subtask: " + ((e as any)?.message || "update failed"), "error");
      return false;
    }
  }

  async function removeLocal(idx: any) {
    if (!writable) return;
    const next = items.filter((_, i) => i !== idx);
    emitLocal(next);
  }

  // Round 14 (C88) — REWRITTEN; root cause of the unreliable reorder: the
  // old pairwise swap wrote the two rows' ARRAY INDICES as their sortOrder.
  // That is only correct when every stored sortOrder already equals its
  // index — historic lists violate that (hard-delete gaps from the bin era,
  // duplicate defaults, createMany offsets), so the swapped pair got small
  // index values that sorted BEFORE untouched rows carrying larger
  // sortOrders: the pane showed the move, the next mount scrambled it. The
  // rewrite swaps, then NORMALIZES sortOrder = index for EVERY row
  // (self-healing legacy lists on first touch) and persists exactly the
  // rows whose sortOrder actually changed. State updates FIRST (optimistic,
  // same C88 rationale as patch); a failed PUT batch rolls everything back.
  async function move(idx: any, dir: any) {
    if (!writable) return;
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= items.length) return;
    const before = items;
    const swapped = items.slice();
    [swapped[idx], swapped[j]] = [swapped[j], swapped[idx]];
    const dirty: any[] = [];
    const next = swapped.map((s, i) => {
      if (Number(s.sortOrder) === i) return s;
      const ns = { ...s, sortOrder: i };
      if (persisted && ns.id) dirty.push(ns);
      return ns;
    });
    if (!persisted) {
      emitLocal(next);
      return;
    }
    applyPersisted(next);
    try {
      /* Phase 30 (D-3) — sequential persists instead of Promise.all: the old
         batch could half-land (sibling PUTs succeeded while one failed),
         then the local rollback diverged from the server until reload. */
      for (const s of dirty) {
        await updateSubtask(s.id, { sortOrder: s.sortOrder });
      }
    } catch (e) {
      console.warn("Subtask reorder failed:", (e as any).message);
      // Re-sync from the server (some PUTs may have landed) and tell the user.
      try {
        const fresh =
          parentTaskId != null
            ? await listSubtasksByTask(parentTaskId)
            : await listSubtasksByEvent(parentEventId);
        if (Array.isArray(fresh)) applyPersisted(fresh);
        else applyPersisted(before);
      } catch {
        applyPersisted(before);
      }
      window.__lxToast?.("Subtask: " + ((e as any)?.message || "reorder failed"), "error");
    }
  }

  /* BRIEF 118 §2 [RULED] — SUBTASKS TAKE THE SAME PILL AS TASKS.
     117 §2a gave the subtask its master's pill and could only turn it between
     two rungs, because `Subtask` had `done Boolean` and no status column; §2
     adds the column and this is the write that uses it.
     BRIEF 136 §1 [RULED] — AND THE SAME MENU. It was `cycleStatus`, turning
     `TK_CYCLE`'s three rungs; the cycle is withdrawn (StatusPill.tsx) and the
     subtask takes whichever code the user chose, from the same
     `pickableStatusCodes` ladder the master pill offers. The pill is ONE
     component and this call site does not grow a second one — it hands over
     options and takes back a code.
     ONE KEY ON THE WIRE: the payload is `{status}` alone, never `{status,
     done}`. The server derives `done` (lib/subtaskStatus.js) and the C68a
     self-toggle carve-out tests the patch SHAPE — one field — so a two-field
     payload would lock an off-team delegatee out of his own subtask. The
     LOCAL row carries both, because every client-side reader of `done` (the
     counter, the due-today buckets, the delegation badge, AgendaView's
     strike-through) reads this array before the server's row comes back.
     THE C76 CONTRACT IS UNCHANGED and its trigger is unchanged with it: the
     completion prompt fires on ENTERING "completed" — now one of five
     destinations rather than one of three. */
  async function setStatus(idx: any, next: string) {
    const cur = items[idx];
    if (!(writable || isMine(cur))) return;
    if (next === subtaskStatusOf(cur)) return;
    const goingToDone = next === "completed";
    // C76 contract preserved: patch resolves only after the PUT landed, so
    // the completion prompt still runs against a PERSISTED tick (its Cancel
    // unticks server-side); the row itself flips optimistically inside.
    const ok = await patch(
      idx,
      { status: next },
      isMine(cur),
      { done: goingToDone },
    );
    if (ok && goingToDone && typeof onCheckDone === "function") {
      // Hand control back to the parent so it can run the canonical
      // completion prompt (C76: Log time / just finish / Cancel-revert).
      onCheckDone({ ...cur, status: next, done: true });
    }
  }

  /* 112 §6 (RULED, Laurent's words) — subtasks stop being archivable at all:
     they are meaningless without their parent task/event, so they get no
     Archive section — rightly so. C75's archive toggle is REPLACED by the
     bin: an effective DELETE in the house removal shape — no confirm dialog,
     row gone, 5s Undo toast whose restore RE-CREATES the row from its
     snapshot (the CCV-party pattern, useConfirmAndUndo label:null). The
     server's DELETE existed all along (routes/subtasks.js, the broadened
     edit gate, chronicled 'purge'); the re-create is the ordinary POST, so
     the restored row carries a new id — which is what "from its snapshot"
     means. Rows ALREADY archived in the data stay dormant: nothing renders
     them (the map skips them) and nothing sweeps them. Local (unsaved) rows
     just get removed, as before. */
  const { confirmAndUndo, undoToast, runUndo } = useConfirmAndUndo();
  const itemsRef = useRef(items);
  itemsRef.current = items;
  function binDelete(idx: number) {
    if (!writable) return;
    const cur = items[idx];
    if (!cur) return;
    if (!persisted || !cur.id) {
      removeLocal(idx);
      return;
    }
    confirmAndUndo({
      label: null,
      toastName: t("subtask.deleted_toast", { fallback: "Subtask deleted" }),
      onConfirm: async () => {
        try {
          await deleteSubtask(cur.id);
          applyPersisted(items.filter((_, i2) => i2 !== idx));
        } catch (e) {
          window.__lxToast?.("Subtask: " + (e instanceof Error ? e.message : "delete failed"), "error");
        }
      },
      undo: {
        snapshot: { ...cur },
        restore: async (snap: Subtask) => {
          try {
            const payload: Record<string, unknown> = {
              /* template rows carry textEn/Fr/Nl and may have no plain text —
                 the create requires one, so the resolved display text backs
                 it; the language fields ride along whole. */
              text: snap.text || resolveSubtaskText(snap),
              dueDate: snap.dueDate || null,
              sortOrder: snap.sortOrder ?? itemsRef.current.length,
            };
            for (const lk of ["textEn", "textFr", "textNl"] as const) {
              if (snap[lk] != null) payload[lk] = snap[lk];
            }
            if (snap.assignedCoId != null) payload.assignedCoId = snap.assignedCoId;
            if (snap.done) payload.done = true;
            if (parentTaskId != null) payload.parentTaskId = parentTaskId;
            if (parentEventId != null) payload.parentEventId = parentEventId;
            const row = await createSubtask(payload);
            applyPersisted([...itemsRef.current, row]);
          } catch (e) {
            window.__lxToast?.("Subtask: " + (e instanceof Error ? e.message : "restore failed"), "error");
          }
        },
      },
    });
  }

  /* 91 §2a/§2c — THE TIMER COLUMN. One fixed slot holding the timer triangle
     and the time-log watch, so every row's description begins at the same x
     whether or not either affordance applies to that row.
     Round 15 (C92) — `wide` lets it grow for the live/paused elapsed readout
     (the fixed width clipped it); idle rows keep the exact C75 geometry.

     BRIEF 92 §3c — AND THE TWO ICONS EACH HOLD THEIR OWN COLUMN INSIDE IT.
     91 centred both buttons in the one 54px cell, which is invisible while both
     are drawn and wrong the moment one is not: a DONE row loses its triangle
     (`rowTimer` requires `!s.done`) and the clock slid to the middle of the
     cell, so a list with open and done rows had its clocks in two places. The
     cell is two sub-slots now (PLAY_W/LOG_W since 115 §4 — the buttons' own
     widths) — triangle left, clock right — each occupying its width empty or
     full. A done row leaves the triangle's place blank and its clock stays
     flush against the description field, exactly where an open row's is.
     Only the triangle's slot grows when `wide`: it is the one that swaps a 14px
     glyph for an elapsed readout, and letting the clock's slot grow with it
     would move the clock off its column for the duration of a timer. */
  /* The three are TYPED and not `any`: §3c added a parameter, the ratchet
     counted it, and a new `any` gets a type rather than a new baseline
     (SESSION_RULES). Both slots take a button or nothing, which is `ReactNode`;
     `wide` is the running/paused flag. */
  const timerCell = (
    timerNode: ReactNode,
    logNode: ReactNode,
    wide?: boolean,
  ) => (
    <span
      style={{
        width: wide ? "auto" : TIMER_W,
        minWidth: TIMER_W,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        /* 115 §4 — tdTimer's own 1px (its clock carries marginLeft: 1). */
        gap: 1,
      }}
    >
      <span
        style={{
          width: wide ? "auto" : PLAY_W,
          minWidth: PLAY_W,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {timerNode}
      </span>
      <span
        style={{
          width: LOG_W,
          minWidth: LOG_W,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {logNode}
      </span>
    </span>
  );

  return (
    <div className="field" style={{ paddingTop: 8, marginTop: 6 }}>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          /* 216 addendum 6 §10 (2) [RULED 81] — SMALLER THAN THE TASK'S OWN TITLE. MEASURED as shown: the task's
             title cell is 11,5 px at 500 (`.tkRow > td`, `tbody td`), this heading was 11 px at 600 and UPPERCASE
             with letter-spacing (`.field label`), which the eye reads as the larger of the two (Laurent, 15/10,
             his screenshot of a collective case's tasks); it takes the field label's own 9,5 px now, once, here —
             so it holds wherever subtasks open (a matter's rows, the Dashboard's cards, the two forms). A
             subtask's text (10,5 px, below) already read smaller than the title. */
          fontSize: FS.s9_5,
          fontWeight: 600,
          color: "var(--ts)",
        }}
      >
        {t("subtask.section_label", { fallback: "Subtasks" })}
        {loading && (
          <span style={{ fontSize: FS.s9, color: "var(--tm)" }}>
            {t("common.loading", { fallback: "loading…" })}
          </span>
        )}
        {/* BRIEF 87 §6 E3 — THE TWO ITALIC LINES MOVE BESIDE THE HEADING.
            They sat UNDER it, stacked with a <br/>, and cost two lines of
            vertical space on the emptiest possible state — the one moment the
            section has nothing else to show and the modal is at its longest.
            On the heading's own line they cost none: the label is already a
            flex row, so they ride in it.

            Joined with a middle dot rather than a <br/> because a line break
            inside a flex row does nothing, and because the two sentences are one
            aside ("nothing here yet, and here is how to get some") rather than
            two facts. `marginLeft: auto` pushes them to the far end so the
            heading keeps its own left edge.

            BRIEF 90 §1b/§1c — TWO CORRECTIONS TO THAT MOVE.

            §1c, and the CAUSE is why it happened: putting the line inside the
            heading put it inside a `<label>`, and `App.css:949` gives every
            `.field label` `text-transform: uppercase`. The line had been
            lower case in its old position and silently became SHOUTED in its
            new one — a property inherited from where it landed, not chosen.
            `textTransform: "none"` takes it back, and the size matches the
            Documents line (FS.s10) so the two asides read as one thing.

            §1b — the wording DIFFERS BY KIND and is two keys, not one
            parameterised key, because the two sentences name different FIELDS:
            a task has a due date, an event has the event date. "Add a date"
            could not say which, and "model subtasks" named a concept the app
            calls nothing anywhere else — the taxonomy attaches them to a TYPE
            and they appear automatically. `kind` is the prop that already
            distinguishes the two hosts. */}
        {subFailed ? (
          <span style={{ marginLeft: "auto", minWidth: 0, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
            <ListLoadContext.Provider value={subLists}>
              <ListNotLoaded lists={[PAGE_LISTS.subtasks]} variant="inline" />
            </ListLoadContext.Provider>
          </span>
        ) : items.length === 0 && !loading && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: FS.s10,
              color: "var(--tm)",
              fontStyle: "italic",
              fontWeight: 400,
              textTransform: "none",
              letterSpacing: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("subtask.empty_state", { fallback: "No subtasks yet." })}{" · "}
            {parentEventId != null
              ? t("subtask.empty_hint_event", {
                  fallback: "Add the event date for automatic subtasks",
                })
              : t("subtask.empty_hint_task", {
                  fallback: "Add a due date for automatic subtasks",
                })}
          </span>
        )}
        {/* BRIEF 108 §1b — the add affordance moves onto the heading's own
            line, right-aligned, under EXACTLY the predicate that rendered the
            permanent add row (`writable`) — a read-only surface gains no
            button the row never had. When the empty-state hint is showing it
            already owns `marginLeft: auto`, so the button just follows it;
            otherwise the button takes the auto margin itself. A button inside
            these flex `<label>`s is the house pattern (MultiSelect's label has
            carried one since B.6). */}
        {/* 110 §1d (RULED) — adding a subtask is normal-mode work, like the
            done checkbox: the add affordance drops its lock term. Rights and
            the Dashboard's view-only rule still gate it.
            116 §1 — REVERSED, at Laurent's own ruling: adding a subtask is
            STRUCTURE after all, so the term comes back. Locked hosts render no
            "+ Add" (and no dialog, below); the done checkbox and the
            description's click-to-edit keep their lock-free arms (109/110 §1c),
            and readOnly still suppresses everything regardless. */}
        {writable && !locked && !readOnly && !subFailed && (
          <AddButton
            label={t("common.add", { fallback: "Add" })}
            onClick={() => setAddOpen(true)}
            style={{
              flexShrink: 0,
              ...(items.length === 0 && !loading ? {} : { marginLeft: "auto" }),
            }}
          />
        )}
      </label>
      {/* §6 E3 — the section's own controls belong INSIDE it, under the heading.
          `headerSlot` is what the two modals hand down: the Template-subtasks
          button, which used to float in a `field` of its own ABOVE the section
          it acts on. Rendered here it reads as part of SUBTASKS, which is what
          it has always been. */}
      {headerSlot ? <div style={{ marginTop: 4 }}>{headerSlot}</div> : null}
      {items.map((s, i) => {
        const key = s.id || s._localId || i;
        const due = s.dueDate ? String(s.dueDate).slice(0, 10) : "";
        /* Round 15 (C92) — full timer state per row when the host passes
           subtaskTimers; the boolean pair only ever knew "some subtask of
           this task is timed", so a running subtask timer could not render
           its elapsed and a paused one was invisible. */
        const _stm = Array.isArray(subtaskTimers)
          ? subtaskTimers.find((x2) => x2 && x2.subtaskId === s.id)
          : null;
        const isThisRunning = _stm
          ? !_stm.paused
          : isTimerRunning && runningSubtaskId === s.id;
        const isThisPaused = !!_stm && !!_stm.paused;
        const displayText = resolveSubtaskText(s);
        const mine = isMine(s);
        const isArch = !!s.archived;
        /* 112 §6 — archived rows render NOWHERE live: the dormant legacy rows
           are skipped, not greyed (C75's dim-in-place is retired). Indices
           stay aligned with `items` because the map itself is unchanged. */
        if (isArch) return null;
        const rowEdit = writable && !isArch && !locked && !readOnly; // date/assignee edits (§1c: structure follows the lock)
        /* 110 §1c — the description is NOT structure: rights-gated only, no
           lock term. Dashboard (readOnly) excepted per §1b. */
        const rowText = writable && !isArch && !readOnly;
        const rowTick = (writable || mine) && !isArch && !readOnly;
        const rowTimer =
          persisted &&
          s.id &&
          !isArch &&
          !s.done &&
          (writable || mine) &&
          typeof onStartTimer === "function";
        const rowLog =
          persisted &&
          s.id &&
          !isArch &&
          (writable || mine) &&
          typeof onLogTime === "function";
        /* §2a — the done checkbox moved to the RIGHT edge, beside the archive
           toggle, so it is written once and placed twice: inside the action
           tail where there is one, and on its own where `hideRowActions`
           removed the tail (the pop-out still ticks).
           BRIEF 117 §2a — and it is a STATUS PILL now, the parent row's own
           (`StatusPill`, extracted from `TkStatusTag`). Same vocabulary
           (`getTaskStatusMeta` over the firm's `task_status` entries), same
           write path (`setStatus` — 136 §1's rename of `cycleStatus`, itself
           118 §2's rename of `toggleDone`; it still awaits the persisted PUT
           and still hands the C76 completion prompt to the host on ENTERING
           "completed"), same MENU (136 §1), same gate
           (`rowTick` — which deliberately carries NO lock term: 109 §1d/110
           keep the done tick live under a padlock, and this pill inherits
           that unchanged). `rowTick` false renders the inert display variant,
           which is what the `disabled` checkbox was. */
        // 118 §2 — the row's rung, from the column when it has one and from
        // `done` when it does not (a row older than §2's backfill).
        const stCode = subtaskStatusOf(s);
        const stCfg = getTaskStatusMeta(stCode, taskStatusEntries);
        const doneBox = (
          <StatusPill
            cfg={stCfg}
            current={stCode}
            minWidth={PILL_W}
            /* 136 §1 — the inert arm keeps a title (the bare label, as it
               always had); the live arm has no `title` so the pill writes the
               one tooltip the menu deserves, once, for both pills. */
            title={rowTick ? undefined : stCfg.label}
            options={rowTick ? stOptions : undefined}
            onPick={rowTick ? (code) => setStatus(i, code) : undefined}
          />
        );
        return (
          <div
            key={key}
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              padding: "3px 0",
              // C75 — archived rows dim in place (kept in the pane, out of
              // every counter), matching the app's archive styling.
              opacity: isArch ? 0.45 : 1,
              fontStyle: isArch ? "italic" : undefined,
            }}
          >
            {/* §2a (1) — assignee, first, as on the parent task row. */}
            {showPicker ? (
              <span
                style={{
                  width: ASSIGN_W,
                  flexShrink: 0,
                  display: "inline-flex",
                  justifyContent: assignEdit === i ? "flex-start" : "center",
                }}
              >
                {assignEdit === i ? (
                  <AssigneePicker
                    co={co}
                    currentUser={currentUser}
                    value={s.assignedCoId}
                    canAssignAll={canAssignAll}
                    className="field-input"
                    style={{
                      width: "100%",
                      margin: 0,
                      padding: "2px 4px",
                      fontSize: FS.s10_5,
                    }}
                    autoOpen
                    onBlur={() => setAssignEdit(null)}
                    onChange={(v) => {
                      setAssignEdit(null);
                      patch(i, {
                        assignedCoId: v ? +v : null,
                      });
                    }}
                  />
                ) : (
                  (() => {
                    /* C64 — resting display is the canonical initials badge
                       (tooltip = full name). Click to reassign; unassigned
                       rows get a dashed "+". Archived rows rest read-only. */
                    const _a = (co || []).find((c: any) => c.id === s.assignedCoId);
                    if (!rowEdit)
                      return _a ? (
                        <InitialsBadge person={_a} isMe={_a.id === currentUser} />
                      ) : null;
                    return _a ? (
                      <span
                        onClick={() => setAssignEdit(i)}
                        style={{ cursor: "pointer", display: "inline-flex" }}
                      >
                        <InitialsBadge person={_a} isMe={_a.id === currentUser} />
                      </span>
                    ) : (
                      <button
                        type="button"
                        title={t("subtask.assignee_tip", {
                          fallback: "Assign subtask",
                        })}
                        onClick={() => setAssignEdit(i)}
                        style={emptyAssignee}
                      >
                        +
                      </button>
                    );
                  })()
                )}
              </span>
            ) : _hasAssignees ? (
              <span
                style={{
                  width: ASSIGN_W,
                  flexShrink: 0,
                  display: "inline-flex",
                  justifyContent: "center",
                }}
              >
                {(() => {
                  const _a = (co || []).find((c: any) => c.id === s.assignedCoId);
                  return _a ? (
                    <InitialsBadge
                      person={_a}
                      isMe={_a.id === currentUser}
                    />
                  ) : null;
                })()}
              </span>
            ) : null}
            {/* §2a (2) — the timer column: triangle AND time-log watch, one
                cell, as the parent row's timer `<td>` draws them. Round 15
                (C92): the running row carries the LIVE elapsed readout in place
                of its triangle (click pauses — startTimer's re-click
                semantics); a paused row shows triangle + static total (click
                resumes); the old Square "stop" glyph is gone — it depicted a
                stop the click never performed. */}
            {_hasTimerCol &&
              timerCell(
                rowTimer ? (
                  /* 110 §1e(vi) — the MAIN ROW's timer chrome (tdTimer,
                     CaseTaskRow), not a solid red chip: transparent, neutral
                     ink at rest (--tp), --tm when paused, the green readout
                     only when RUNNING. Icon at the row's own I.s = 12. */
                  <button
                    type="button"
                    className="btn"
                    style={{
                      ...timerBtn,
                      ...(isThisRunning
                        ? {
                            background: "var(--gs)",
                            color: CT.c_fff,
                            fontFamily: "'JetBrains Mono',monospace",
                            fontWeight: 600,
                            fontSize: FS.s9_5,
                            lineHeight: 1,
                            padding: "3px 4px",
                          }
                        : isThisPaused
                          ? {
                              color: "var(--tm)",
                              lineHeight: 1,
                              padding: "3px 4px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 2,
                            }
                          : {}),
                    }}
                    title={
                      isThisRunning
                        ? t("subtask.stop_timer_tip", {
                            fallback: "Stop subtask timer",
                          })
                        : t("subtask.start_timer_tip", {
                            fallback: "Start subtask timer",
                          })
                    }
                    onClick={() => onStartTimer(s)}
                  >
                    {isThisRunning ? (
                      <SelfTick active>
                        {() =>
                          fmtElapsed(
                            _stm.elapsed + (Date.now() - _stm.startedAt),
                          )
                        }
                      </SelfTick>
                    ) : (
                      <>
                        <Play size={12} />
                        {isThisPaused && (
                          <span
                            style={{
                              fontSize: FS.s9_5,
                              fontFamily: "'JetBrains Mono',monospace",
                              fontWeight: 600,
                              color: "var(--tm)",
                            }}
                          >
                            {fmtElapsed(_stm.elapsed)}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                ) : null,
                rowLog ? (
                  /* 110 §1e(vi) — the main row's log-time chrome: transparent,
                     --ts ink, no chip. */
                  <button
                    type="button"
                    className="btn"
                    style={{
                      padding: "1px 3px",
                      background: "transparent",
                      color: "var(--ts)",
                      border: "none",
                      borderRadius: RAD.r4,
                      cursor: "pointer",
                      lineHeight: 0,
                    }}
                    title={t("subtask.log_time_tip", {
                      fallback: "Log time on this subtask",
                    })}
                    onClick={() => onLogTime(s)}
                  >
                    <Clock size={12} />
                  </button>
                ) : null,
                isThisRunning || isThisPaused,
              )}
            {/* §2a (3) — description. Adaptive, and the only column that is.
                110 §1c — the notes pattern (RULED): quiet text in EVERY state
                (the permanent input is gone — the drawer stops shouting, §1e's
                point too); a click, rights permitting and lock or no lock,
                reveals the contrasting field; blur/Enter commit through the
                existing patch; Escape reverts via the ref (109's blur lesson).
                Read-only rows keep the truncating span (the C70 fix — a
                min-width:0 input collapsed to nothing in the narrow pop-out
                and read as "empty"). */}
            {textEdit === key && rowText ? (
              <input
                className="field-input"
                autoFocus
                style={{
                  flex: 1,
                  minWidth: 90,
                  padding: "2px 6px",
                  fontSize: FS.s11,
                  textDecoration: s.done ? "line-through" : "none",
                  opacity: s.done ? 0.55 : 1,
                }}
                defaultValue={displayText}
                key={`${key}:${displayText}`}
                onBlur={(e) => {
                  if (cancelTextRef.current) {
                    cancelTextRef.current = false;
                    setTextEdit(null);
                    return;
                  }
                  const v = e.target.value.trim();
                  if (v && v !== displayText) patch(i, { text: v });
                  setTextEdit(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  } else if (e.key === "Escape") {
                    cancelTextRef.current = true;
                    (e.target as HTMLInputElement).blur();
                  }
                }}
              />
            ) : (
              <span
                title={displayText}
                onClick={
                  rowText
                    ? (e) => {
                        e.stopPropagation();
                        setTextEdit(key);
                      }
                    : undefined
                }
                style={{
                  flex: 1,
                  minWidth: 40,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  /* 216 addendum 6 §10 (2) [RULED 81] — a subtask's text smaller than its task's title. MEASURED as
                     shown: the title cell computes to 10,67 px (`tbody td`: the base font, pinned at 13 px, × 0,821)
                     and this text was the 11 px token — larger by a third of a pixel; the 10 px token now, visibly
                     below it. The editing input beside keeps its 11 px. */
                  fontSize: FS.s10,
                  padding: "2px 0",
                  textDecoration: s.done ? "line-through" : "none",
                  opacity: s.done ? 0.55 : 1,
                  cursor: rowText ? "text" : undefined,
                }}
              >
                {displayText}
              </span>
            )}
            {/* §2a (4) — due date, fixed-width date column.
                BRIEF 95 §3a(a) — DD/MM/YY, so 8 characters and not 10. */}
            {rowEdit ? (
              <DateField
                className="field-input stDate"
                wrapStyle={{ width: DATE_W, flexShrink: 0 }}
                style={{
                  width: "100%",
                  // C83iii — 2px sides: the width budget is exactly
                  // 8ch + padding + border now.
                  padding: "2px 2px",
                  fontSize: FS.s11,
                }}
                value={due}
                onClick={openPicker}
                onChange={(v) => patch(i, { dueDate: v || null })}
              />
            ) : (
              <span
                style={{
                  width: DATE_W,
                  flexShrink: 0,
                  fontSize: FS.s11,
                  color: "var(--ts)",
                  textAlign: "center",
                  fontFamily: "'JetBrains Mono',monospace",
                }}
              >
                {/* §3a(a) — the read-only twin of the field beside it: the same
                    eight characters, so the column does not change width with
                    the row's edit state. */}
                {due ? dmy2(due) : ""}
              </span>
            )}
            {/* §2a (5) — THE TAIL. §2c puts the done checkbox and the archive
                toggle at the far right, adjacent, archive outermost. The
                reorder chevrons keep their place in this fixed block, ahead of
                the pair, which is the only arrangement that leaves the six
                columns the brief names in the order it names them AND leaves
                archive outermost — they trail after the archive today, where
                "far right" would be theirs and not its.
                Hidden entirely in the pop-out/column host (C83ii), where the
                checkbox is then the last column and stands on its own. */}
            {_rowActions ? (
              <span
                style={{
                  width: TAIL_W,
                  flexShrink: 0,
                  display: "flex",
                  gap: 4,
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  style={chevStyle(i === 0)}
                  title={t("subtask.move_up_tip", { fallback: "Move up" })}
                  onClick={() => move(i, "up")}
                  disabled={i === 0}
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  style={chevStyle(i === items.length - 1)}
                  title={t("subtask.move_down_tip", { fallback: "Move down" })}
                  onClick={() => move(i, "down")}
                  disabled={i === items.length - 1}
                >
                  <ChevronDown size={14} />
                </button>
                {doneBox}
                {/* 112 §6 — the archive toggle is REPLACED by the bin: a
                    subtask is meaningless without its parent, so it deletes
                    (house shape, Undo re-creates). Same slot, same gates —
                    the tail only renders unlocked and outside the Dashboard's
                    view-only drawers (_rowActions), which is where the brief
                    wants the bin absent. */}
                <button
                  type="button"
                  className="btn btn-danger"
                  style={iconBtn}
                  title={t("subtask.delete_tip", {
                    fallback: "Delete subtask",
                  })}
                  onClick={() => binDelete(i)}
                >
                  <Trash2 size={12} />
                </button>
              </span>
            ) : (
              doneBox
            )}
          </div>
        );
      })}
      {/* BRIEF 108 §1a/§1c — the permanent add row is gone; adding goes
          through a small ModalLayout behind the heading's "+ Add", carrying
          the SAME fields the row had (description · due date · assignee — the
          row's draft carried one since Round 16 C100) and the same add():
          Enter in the description still adds, the staged assignee still rides
          the create, a failed create keeps the dialog open. On add: close
          (§1c's decision — one add per opening); on cancel: the draft is
          discarded. The row's ASSIGN_W/TIMER_W/TAIL_W spacers do NOT move
          here — they existed only to line the row up under the list, and the
          list rows keep those constants for their own columns (§1d). The
          modal's fields are ordinary live fields, so 92 §3d's mutedField
          deliberately stays behind: a dialog's draft IS work in progress.
          zIndex 300 stacks above a host modal, the same step
          useConfirmAndUndo takes. */}
      {/* 110 §1d — the dialog follows its button out of the lock gate.
          116 §1 — and back into it with the button: the two conditions are one
          affordance, so they carry one predicate. A drawer locked while its
          dialog is open closes the dialog, which is the correct reading of a
          lock (it is not a confirmation prompt). */}
      {writable && !locked && !readOnly && addOpen && (
        <ModalLayout
          title={t("subtask.add_title", { fallback: "Add subtask" })}
          onClose={() => {
            setAddOpen(false);
            setDraft({ text: "", dueDate: "", assignedCoId: null });
          }}
          onSave={add}
          saveDisabled={!draft.text.trim()}
          saveLabel={t("common.add", { fallback: "Add" })}
          maxWidth={400}
          zIndex={Z.modal2}
          escToClose
        >
          <div className="field">
            <label>
              {t("common.description", { fallback: "Description" })}
            </label>
            <input
              className="field-input"
              autoFocus
              placeholder={t("subtask.add_placeholder", {
                fallback: "Description of the subtask",
              })}
              value={draft.text}
              onChange={(e) =>
                setDraft((d: any) => ({ ...d, text: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
            />
          </div>
          <div className="field">
            <label>{t("field.due_date", { fallback: "Due Date" })}</label>
            <DateField
              className="field-input stDate"
              style={{ width: "100%" }}
              value={draft.dueDate}
              onClick={openPicker}
              onChange={(v) => setDraft((d: any) => ({ ...d, dueDate: v }))}
            />
          </div>
          {showPicker && (
            <div className="field">
              <label>
                {t("field.assigned_to", { fallback: "Assigned To" })}
              </label>
              {/* 160 §4 — THE MENU MUST CLEAR THE MODAL THAT HOSTS IT. This
                  modal is <ModalLayout zIndex={300}> (:1212); the picker's
                  MenuSelect portals its list to document.body, so it stacks
                  against the PAGE and not against its trigger's ancestors, and
                  the idiom's default 201 put it under this modal's own
                  backdrop. MenuSelect's prop comment already stated the
                  contract — "the document modal sits at 300 and passes 301" —
                  and AssigneePicker simply had no way to speak it. */}
              <AssigneePicker
                co={co}
                currentUser={currentUser}
                value={draft.assignedCoId}
                canAssignAll={canAssignAll}
                className="field-input"
                style={{ width: "100%" }}
                zIndex={Z.modal2Menu}
                onChange={(v: string) =>
                  setDraft((d: any) => ({
                    ...d,
                    assignedCoId: v ? +v : null,
                  }))
                }
              />
            </div>
          )}
        </ModalLayout>
      )}
      {/* 112 §6 — the bin's 5s Undo toast (house removal shape). */}
      <UndoToast toast={undoToast} onUndo={runUndo} />
    </div>
  );
}

const iconBtn = { padding: "3px 6px", fontSize: FS.s10, lineHeight: 0 };
/* 110 §1e(vi) — the main task row's resting timer chrome (tdTimer,
   CaseTaskRow): transparent, primary ink, no chip. The running/paused
   branches at the call site override exactly what tdTimer overrides. */
const timerBtn = {
  padding: "1px 4px",
  background: "transparent",
  color: "var(--tp)",
  border: "none",
  borderRadius: RAD.r4,
  cursor: "pointer",
  lineHeight: 1,
};


/* BRIEF 91 §2b, CORRECTED BY BRIEF 92 §3a — THE EMPTY ASSIGNEE SLOT.
 *
 * 91 §2b moved it off `1px dashed var(--br)` with `var(--tm)` ink — the border
 * colour and the muted-text colour, two tokens that exist to RECEDE — and into
 * the Badges family, which was right. It then picked the wrong half of that
 * family and its own reasoning is why: *"the SAME colour the filled badge
 * already uses"* named `--bf`, and `--bf` is **"Badge fallback TEXT"**
 * (themeDerivation.ts:72) — the badge's FOREGROUND, light ink drawn ON a disc.
 * Alone on a card it is light-on-light, and the glance found it invisible.
 *
 * `--cb` is "Collaborator badge", the Badges group's BACKGROUND and the colour
 * a filled badge actually SHOWS (`InitialsBadge`, SharedUI.tsx:
 * `background: "var(--cb)"`). So 91's argument now holds for real: the empty
 * slot is drawn in the colour of the thing it becomes.
 *
 * DOTTED — 92 §3a made it solid and BRIEF 93 §2 takes that half back. The colour
 * was the fix; the solid border rode along with it, and once the colour is stark
 * the dash carries on its own. An empty slot should read as EMPTY: a solid ring
 * is a badge with nobody in it, a dashed one is a place FOR somebody. It only
 * ever needed to be visible, not filled. (The `--cb` half of this stood until
 * 108 §1e below.)
 *
 * 20px stays, and 91's reason stays with it: `InitialsBadge`'s default size, so
 * nothing in the column moves when a subtask is assigned.
 */
/* BRIEF 108 §1e — `--cb` (the Badges background, 92 §3a's pick) measured too
 * faint as an outline-only ring: a filled disc SHOWS that colour over a whole
 * circle; a 1px dashed ring of it nearly vanishes. The drawer icons settled
 * this question for row affordances-with-something-behind-them at
 * DRAWER_ICON_HAS = var(--tp) (RowDrawerToggle.tsx:95) — the app's primary
 * text ink on the app's row background, the pairing every title on these rows
 * already renders, legible in both themes by construction. Circle AND glyph
 * move to that ink; the background stays none and the dash stays (93 §2: an
 * empty slot reads as empty). */
const emptyAssignee = {
  width: 20,
  height: 20,
  borderRadius: RAD.pill,
  border: "1px dashed var(--tp)",
  background: "none",
  color: "var(--tp)",
  fontSize: FS.s11,
  lineHeight: 1,
  padding: 0,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
} as const;
function chevStyle(disabled: any) {
  return {
    background: "none",
    border: "none",
    // C88 — a real hit target: the old 12px zero-padding chevron was easy
    // to miss entirely, which read as "clicked and nothing happened".
    padding: "3px 2px",
    lineHeight: 0,
    color: disabled ? "var(--br)" : "var(--tm)",
    cursor: disabled ? "default" : "pointer",
  };
}
